import toast from "react-hot-toast";
import store from "../store";
import ESCPOSFormatter from "../utils/formartEscPosPrint";
import {
  isReactNativeWebView,
  printEscPosBase64ViaReactNative,
} from "../utils/bluetoothBridge";
import {
  isWebBluetoothSupported,
  printEscPosViaWebBluetooth,
  printEscPosToKnownDevice,
} from "../utils/webBluetoothEscPos";
import {
  defaultPrinterPrintConfig,
  normalizePrinterPrintConfig,
} from "../utils/thermalPrinterConfig.js";

const BILL_PRINTER_LOCATIONS = new Set(["bill", "all"]);

const isBillPrinterLocation = (location) =>
  BILL_PRINTER_LOCATIONS.has(location);

/** Configured Bluetooth printers for bill (may or may not be "connected" in cloud). */
const getBluetoothBillPrinterConfigs = (printers = store.printers) =>
  (printers || []).filter(
    (printer) =>
      printer?.type === "bluetooth" && isBillPrinterLocation(printer.location)
  );

const getConnectedBluetoothBillPrinters = (printers = store.printers) =>
  getBluetoothBillPrinterConfigs(printers).filter((p) => p?.isConnected);

const hasConnectedBluetoothBillPrinter = (printers = store.printers) =>
  getConnectedBluetoothBillPrinters(printers).length > 0;

const hasBluetoothBillPrinterConfigured = (printers = store.printers) =>
  getBluetoothBillPrinterConfigs(printers).length > 0;

/** Encoder options when printing from the browser without a saved Bluetooth printer. */
const DEFAULT_BROWSER_THERMAL_PRINTER = defaultPrinterPrintConfig({
  type: "bluetooth",
  location: "bill",
  deviceName: "",
  innerMacAddress: "web-bluetooth",
  isConnected: true,
  name: "Web Bluetooth",
  id: "web-bluetooth-thermal",
});

/**
 * Pure webapp (Chrome): allow Web Bluetooth bill print even with no printer row in Settings.
 * Enable with build flag or once in the browser console:
 *   localStorage.setItem("qrapid_webBluetoothThermalBill", "1")
 */
function isWebBluetoothThermalBillBypassEnabled() {
  try {
    if (import.meta.env?.VITE_WEB_BLUETOOTH_THERMAL_BILL === "true") {
      return true;
    }
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("qrapid_webBluetoothThermalBill") === "1";
  } catch {
    return false;
  }
}

function isBrowserContext() {
  return typeof window !== "undefined" && !isReactNativeWebView();
}

/**
 * @param {unknown} encoded
 * @returns {Uint8Array}
 */
function toUint8Array(encoded) {
  if (encoded instanceof Uint8Array) return encoded;
  if (ArrayBuffer.isView(encoded)) {
    return new Uint8Array(
      encoded.buffer,
      encoded.byteOffset,
      encoded.byteLength
    );
  }
  if (encoded instanceof ArrayBuffer) return new Uint8Array(encoded);
  if (typeof Buffer !== "undefined" && encoded && typeof encoded === "object") {
    try {
      if (Buffer.isBuffer?.(encoded)) return new Uint8Array(encoded);
    } catch {
      /* ignore */
    }
  }
  return new TextEncoder().encode(String(encoded ?? ""));
}

/**
 * Bill ESC/POS bytes from the same encoder used for QPrinter / server print.
 */
function getBillEscPosBytes(orderData, printerConfig) {
  return toUint8Array(
    ESCPOSFormatter.printBill(orderData, normalizePrinterPrintConfig(printerConfig))
  );
}

/**
 * Previously allowed Web Bluetooth devices for this origin (often no new user gesture).
 * @returns {Promise<BluetoothDevice|null>}
 */
async function getPreviouslyAllowedWebBluetoothDevice() {
  if (!navigator.bluetooth?.getDevices) return null;
  try {
    const list = await navigator.bluetooth.getDevices();
    return list?.length ? list[0] : null;
  } catch {
    return null;
  }
}

async function printBillBytesViaWebBluetooth(bytes, printerConfig) {
  const existing = await getPreviouslyAllowedWebBluetoothDevice();
  if (existing) {
    await printEscPosToKnownDevice(existing, bytes);
    return;
  }

  // Always use acceptAllDevices (no namePrefix filters). Saved "deviceName"
  // often does not match the BLE advertised name, which made Chrome show an
  // empty list or hide thermal printers.
  await printEscPosViaWebBluetooth(bytes, {});
}

function isUserGestureError(err) {
  const msg = String(err?.message || "").toLowerCase();
  return (
    err?.name === "InvalidStateError" ||
    err?.name === "SecurityError" ||
    msg.includes("user gesture") ||
    msg.includes("user gesture is required")
  );
}

/**
 * Browser: Web Bluetooth + raw ESC/POS. React Native WebView: base64 payload to native.
 */
const printBillViaBluetooth = async (orderData, printers = store.printers) => {
  const bluetoothConfigs = getBluetoothBillPrinterConfigs(printers);

  let printerForEncode;
  let targets;

  if (bluetoothConfigs.length > 0) {
    const preferConnected = getConnectedBluetoothBillPrinters(printers);
    targets =
      preferConnected.length > 0 ? preferConnected : bluetoothConfigs;
    printerForEncode = targets[0];
  } else if (
    isBrowserContext() &&
    isWebBluetoothSupported() &&
    isWebBluetoothThermalBillBypassEnabled()
  ) {
    targets = [DEFAULT_BROWSER_THERMAL_PRINTER];
    printerForEncode = DEFAULT_BROWSER_THERMAL_PRINTER;
  } else {
    return false;
  }

  let bytes;
  try {
    bytes = getBillEscPosBytes(orderData, printerForEncode);
  } catch (e) {
    console.error("Bill ESC/POS encode failed:", e);
    toast.error("Could not build bill for printer.");
    return false;
  }

  if (bytes.length === 0) {
    console.warn("Bill ESC/POS payload is empty");
    return false;
  }

  if (isWebBluetoothSupported()) {
    try {
      await printBillBytesViaWebBluetooth(bytes, printerForEncode);
      return true;
    } catch (e) {
      console.error("Web Bluetooth bill print failed:", e);
      if (e?.message?.includes("User cancelled") || e?.name === "NotFoundError") {
        toast.error("Bluetooth print cancelled or no device selected.");
      } else if (isUserGestureError(e)) {
        toast.error(
          "Bluetooth needs a direct tap: try Print Bill again, or pair the printer once in Chrome (same site) so it can reuse the device."
        );
      } else {
        toast.error(`Bluetooth print failed: ${e?.message || e}`);
      }
      return false;
    }
  }

  if (isReactNativeWebView()) {
    try {
      for (const printer of targets) {
        const perPrinterBytes = getBillEscPosBytes(orderData, printer);
        await printEscPosBase64ViaReactNative(perPrinterBytes, printer);
      }
      return true;
    } catch (e) {
      console.error("RN Bluetooth bill print failed:", e);
      toast.error("Could not send bill to the app printer.");
      return false;
    }
  }

  console.warn(
    "Bluetooth bill print skipped: Web Bluetooth unavailable and not in React Native WebView."
  );
  if (bluetoothConfigs.length > 0) {
    toast.error(
      "Bluetooth bill printer is set up but cannot print from this browser. Reconnect the printer or use the QRapid app."
    );
  }
  return false;
};

export {
  isBillPrinterLocation,
  getBluetoothBillPrinterConfigs,
  getConnectedBluetoothBillPrinters,
  hasConnectedBluetoothBillPrinter,
  hasBluetoothBillPrinterConfigured,
  printBillViaBluetooth,
  getBillEscPosBytes,
};
